import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import Flexbox from "flexbox-react";
import {
  Card,
  Button,
  InputGroup,
  Dialog,
  FormGroup,
  Alert,
  Spinner,
  Classes
} from "@blueprintjs/core";
import ToastStore from "../../stores/ToastStore";
import ConfirmationDialog from "../../views/ConfirmationDialog";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import EditIcon from "../../assets/editIcon.svg";
import DeleteIcon from "../../assets/Delete.svg";
import "./ReviewQuestionCategories.css";

const CATEGORY_COLORS = [
  "#FF5C8A",
  "#0EA5E9",
  "#A78BFA",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#14B8A6",
  "#6366F1",
  "#EF4444",
  "#84CC16"
];

const ReviewQuestionCategories = () => {

  const [categories, setCategories] = useState([{ id: 1, name: "Category 1" }, { id: 2, name: "Category 2" }, { id: 3, name: "Category 3" }]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [formData, setFormData] = useState({ name: "" });

  useEffect(() => {
    fetchCategories();
  }, []);

  // inject drag cursor CSS
  useEffect(() => {
    try {
      const styleId = 'review-question-drag-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = '.drag-drop-pointer, .drag-drop-pointer * { cursor: move !important; } .dragging-row { opacity: 0.6; }';
        document.head.appendChild(style);
      }
    } catch (err) {
    }
  }, []);

  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = (e, category, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ categoryId: category.id, fromIndex: index }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(category.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    try {
      document.body.classList.add('drag-drop-pointer');
      document.body.style.cursor = 'pointer';
    } catch (err) {}
  };

  const handleDragLeave = (e) => {
    try {
      document.body.classList.remove('drag-drop-pointer');
      document.body.style.cursor = '';
    } catch (err) {}
  };

  const handleDragEnd = (e) => {
    setDraggingId(null);
    try {
      document.body.classList.remove('drag-drop-pointer');
      document.body.style.cursor = '';
    } catch (err) {}
  };

  const handleDropOnIndex = async (e, toIndexOrCategoryId, maybeCategoryId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const data = e.dataTransfer.getData('text/plain');
      if (!data) return;
      const { categoryId, fromIndex } = JSON.parse(data);
      if (!categoryId) return;
      let toIndex = 0;
      if (maybeCategoryId !== undefined) {
        toIndex = categories.findIndex(c => String(c.id) === String(maybeCategoryId));
        if (toIndex === -1) toIndex = categories.length;
      } else {
        toIndex = toIndexOrCategoryId;
      }

      const movingIdx = categories.findIndex(c => String(c.id) === String(categoryId));
      if (movingIdx === -1) return;
      if (movingIdx === toIndex || movingIdx === toIndex - 1) return;

      const newCats = [...categories];
      const [moved] = newCats.splice(movingIdx, 1);
      const insertIndex = movingIdx < toIndex ? toIndex - 1 : toIndex;
      newCats.splice(insertIndex, 0, moved);

      setCategories(newCats);

      const position = insertIndex + 1;
      await axios.put(`${Cookies.get("apiEnv")}/question_categories/${categoryId}`, { position });
      await fetchCategories();

    } catch (err) {
      console.error('Error reordering categories', err);
      fetchCategories();
    } finally {
      try {
        document.body.classList.remove('drag-drop-pointer');
        document.body.style.cursor = '';
      } catch (err) {}
      setDraggingId(null);
    }
  };

  const getCategoryColor = id => {
    return CATEGORY_COLORS[
      id % CATEGORY_COLORS.length
    ];
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Cookies.get("apiEnv")}/question_categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: ""
    });
    setIsDialogOpen(true);
  };

  const handleEditCategory = category => {
    setEditingCategory(category);
    setFormData({
      name: category.name
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;

    try {
      if (editingCategory) {
        await axios.put(
          `${Cookies.get("apiEnv")}/question_categories/${editingCategory.id}`,
          {
            name: formData.name
          }
        );
      } else {
        await axios.post(`${Cookies.get("apiEnv")}/question_categories`, {
          name: formData.name
        });
      }
      setIsDialogOpen(false);
      ToastStore.showToast("Department added successfully", "success");
      fetchCategories();
    } catch (error) {
      const apiError = error?.response?.data?.error;

  const message = Array.isArray(apiError)
    ? apiError.join(", ")
    : apiError || "Failed to add department";

  ToastStore.showToast(message, "danger");
  setIsDialogOpen(false);
  console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!deleteCategoryId) return;
    try {
      await axios.delete(
        `${Cookies.get("apiEnv")}/question_categories/${deleteCategoryId}`
      );
      setDeleteCategoryId(null);
      setIsDeleteDialogOpen(false);
      ToastStore.showToast("Department deleted successfully", "success");
      fetchCategories();
    } catch (error) {
      console.error(error);
      setIsDeleteDialogOpen(false);
      ToastStore.showToast("Failed to delete department", "danger");
    }
  };

  const filteredCategories = useMemo(() => {
    return categories?.length > 0 ? categories.filter(category =>
      category.name
        .toLowerCase()
        .includes(search.toLowerCase())
    ) : [];
  }, [categories, search]);

  return (
    <div className="review-question-categories">
      <div className="categories-card">
        <Flexbox
          justifyContent="space-between"
          alignItems="center"
          style={{ width: '70%' }}
        >
          <div>
            <h2 className="page-title">Departments </h2>
          </div>

          <Flexbox alignItems="center" gap="12px" >
            <FilledButton
              onClick={handleAddCategory}
              text="Add New Department"
            />
          </Flexbox>
        </Flexbox>

        <div className="categories-table">
          <div className="table-header">
            <div>NAME</div>
            <div style={{ paddingRight: '6px' }}>ACTIONS</div>
          </div>

          {loading ? (
            <div className="loading-container">
              <Spinner size={30} />
            </div>
          ) : filteredCategories.length ? (
            filteredCategories.map((category, idx) => (
              <div
                className={`table-row ${String(draggingId) === String(category.id) ? 'dragging-row' : ''}`}
                key={category.id}
                draggable
                onDragStart={(e) => handleDragStart(e, category, idx)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDropOnIndex(e, idx, category.id)}
                onDragEnd={handleDragEnd}
              >
                <Flexbox alignItems="center">
                  {/* DRAG HANDLE (9 dots) */}
                  <div
                    className="department-drag-handle"
                    title="Drag to move or reorder department"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      marginRight: 8,
                      cursor: "grab",
                      flex: "0 0 auto"
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g fill="#8a8f98">
                        <circle cx="3" cy="3" r="1.2" />
                        <circle cx="8" cy="3" r="1.2" />
                        <circle cx="13" cy="3" r="1.2" />

                        <circle cx="3" cy="8" r="1.2" />
                        <circle cx="8" cy="8" r="1.2" />
                        <circle cx="13" cy="8" r="1.2" />

                        <circle cx="3" cy="13" r="1.2" />
                        <circle cx="8" cy="13" r="1.2" />
                        <circle cx="13" cy="13" r="1.2" />
                      </g>
                    </svg>
                  </div>
                  <span className="category-name">
                    {category.name}
                  </span>
                </Flexbox>

                <Flexbox gap="8px">
                  <Button
                    icon={<img src={EditIcon} style={{ width: 18, height: 18 }} />}
                    minimal
                    onClick={() => handleEditCategory(category)}
                  />
                  <Button
                    icon={<img src={DeleteIcon} style={{ width: 18, height: 18 }} />}
                    minimal
                    intent="danger"
                    onClick={() => {
                      setDeleteCategoryId(category.id);
                      setIsDeleteDialogOpen(true);
                    }}
                  />
                </Flexbox>
              </div>
            ))
          ) : (
            <div className="empty-state">
              No Department found
            </div>
          )}
        </div>

        {/* allow dropping at end to append */}
        <div
          style={{ minHeight: 20 }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDropOnIndex(e, categories.length)}
        />
      </div>

      <Dialog
        portalContainer={document.body}
        isOpen={isDialogOpen}
        onClose={() =>
          setIsDialogOpen(false)
        }
        title={
          editingCategory
            ? "Edit Department"
            : "Add Department"
        }
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="Department Name">
            <InputGroup
              value={formData.name}
              placeholder="Enter department name"
              onChange={e =>
                setFormData({
                  name: e.target.value
                })
              }
            />
          </FormGroup>
        </div>

        <Flexbox justifyContent="flex-start" gap="10px" style={{ marginLeft: "20px" }}>
          <FilledButton onClick={handleSave}
            style={{ marginRight: "10px" }}
            text={editingCategory
              ? "Update"
              : "Add"}
          />
          <OutlinedButton
            onClick={() => setIsDialogOpen(false)}
            text="Cancel"
          />
        </Flexbox>
      </Dialog>
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Department"
        message="Are you sure you want to delete this department"
        confirmText="Delete"
        confirmIntent="danger"
      />
    </div>
  );
};

export default ReviewQuestionCategories;